// Quack, this is a duck :) https://github.com/erikras/ducks-modular-redux
import { combineReducers } from 'redux';
import { toastr } from 'react-redux-toastr';
import forEach from 'lodash/forEach';
import values from 'lodash/values';
import { get } from '../../shared/http/request';
import queryString from 'query-string';
import { handleApiError } from '../../shared/http/error';
import moment from 'moment';

/** ************************
 * Manifests redux bundle
 ************************ */
// API
const API_BASE = '/api/manifests/';

// Actions
const LOAD_MANIFEST_PENDING = 'Fetching manifest by ID';
const LOAD_MANIFEST_FULFILLED = 'Manifest fetched';
const LOAD_MANIFEST_REJECTED = 'Failed to fetch manifest';
const LOAD_MANIFESTS_PENDING = 'Fetching manifests';
const LOAD_MANIFESTS_FULFILLED = 'Manifests fetched';
const LOAD_MANIFESTS_REJECTED = 'Failed to fetch manifests';
const LOAD_STOP_MANIFESTS_PENDING = 'Fetching manifests by stop ID';
const LOAD_STOP_MANIFESTS_FULFILLED = 'Manifests by stop fetched';
const LOAD_STOP_MANIFESTS_REJECTED = 'Failed to fetch manifests by stop';
const LOAD_ABN_MANIFESTS_PENDING = 'Fetching abnormality manifests';
const LOAD_ABN_MANIFESTS_FULFILLED = 'Abnormality manifests fetched';
const LOAD_ABN_MANIFESTS_REJECTED = 'Failed to fetch abnormality manifests';
const CLEAR_ABN_MANIFESTS = 'Cleared list of abnormality manifests';
const CLEAR_STOP_MANIFESTS = 'Cleared list of manifest by stop';
const CLEAR_MANIFESTS = 'Cleared list of manifests';
const CLEAR_ERROR_MESSAGE = 'Cleared error messages';

// Saved search
const STORE_SEARCH_MANIFESTS = 'Stored search form';

// Default state
export const defaultManifestState = {
    abnormalities: [],
    kanbans: [],
    recoveryActions: [],
    runs: [],
};

// Reducers
const entities = (state = null, action) => {
    switch (action.type) {
        case LOAD_MANIFEST_FULFILLED:
            return Object.assign({}, state, {
                [action.manifest.manifestId]: action.manifest,
            });

        case LOAD_MANIFESTS_FULFILLED:
            const manifestsById = action.manifests.map(manifest => ({
                [manifest.manifestId]: manifest,
            }));
            return Object.assign({}, state, ...manifestsById);

        case LOAD_STOP_MANIFESTS_FULFILLED:
            const manifestsByStopId = action.manifests.map(manifest => ({
                [manifest.manifestId]: manifest,
            }));
            return Object.assign({}, state, ...manifestsByStopId);

        case LOAD_MANIFESTS_PENDING:
        case LOAD_MANIFESTS_REJECTED:
        case CLEAR_MANIFESTS:
            return null;

        default:
            return state;
    }
};

const ids = (state = null, action) => {
    switch (action.type) {
        case LOAD_STOP_MANIFESTS_FULFILLED:
            const manifestsByStopId = action.manifests;
            const ids = [];
            forEach(manifestsByStopId, man => {
                ids.push(man.manifestId);
            });
            return Object.assign([], state, ids);

        case CLEAR_STOP_MANIFESTS:
            return null;

        case LOAD_STOP_MANIFESTS_PENDING:
            return null;

        case LOAD_STOP_MANIFESTS_REJECTED:
            return null;

        default:
            return state;
    }
};

const byRun = (state = [], action) => {
    switch (action.type) {
        case LOAD_ABN_MANIFESTS_FULFILLED:
            return Object.assign([], state, action.manifests);
        case LOAD_ABN_MANIFESTS_PENDING:
        case LOAD_ABN_MANIFESTS_REJECTED:
            return [];
        case CLEAR_ABN_MANIFESTS:
            return [];
        default:
            return state;
    }
};

const isFetching = (state = false, action) => {
    switch (action.type) {
        case LOAD_MANIFEST_PENDING:
        case LOAD_MANIFESTS_PENDING:
        case LOAD_STOP_MANIFESTS_PENDING:
        case LOAD_ABN_MANIFESTS_PENDING:
            return true;
        case LOAD_MANIFEST_FULFILLED:
        case LOAD_MANIFEST_REJECTED:
        case LOAD_MANIFESTS_FULFILLED:
        case LOAD_MANIFESTS_REJECTED:
        case LOAD_STOP_MANIFESTS_FULFILLED:
        case LOAD_STOP_MANIFESTS_REJECTED:
        case LOAD_ABN_MANIFESTS_FULFILLED:
        case LOAD_ABN_MANIFESTS_REJECTED:
            return false;
        default:
            return state;
    }
};

const error = (state = null, action) => {
    switch (action.type) {
        case LOAD_MANIFEST_REJECTED:
        case LOAD_MANIFESTS_REJECTED:
        case LOAD_STOP_MANIFESTS_REJECTED:
        case LOAD_ABN_MANIFESTS_REJECTED:
            return true;
        case LOAD_MANIFEST_PENDING:
        case LOAD_MANIFEST_FULFILLED:
        case LOAD_MANIFESTS_PENDING:
        case LOAD_MANIFESTS_FULFILLED:
        case LOAD_STOP_MANIFESTS_PENDING:
        case LOAD_STOP_MANIFESTS_FULFILLED:
        case LOAD_ABN_MANIFESTS_PENDING:
        case LOAD_ABN_MANIFESTS_FULFILLED:
        case CLEAR_ERROR_MESSAGE:
            return false;
        default:
            return state;
    }
};

export const INITIAL_MANIFEST_SEARCH_FORM = {
    emcCode: '',
    emcDockCode: '',
    emcPlantCode: '',
    includeGhosts: true,
    kanbanNumber: '',
    manifestNumber: '',
    pLaneExit: '',
    routeCode: '',
    runDate: '',
    runSequence: '',
    supplierCode: '',
    supplierPlantCode: '',
};
const manifestsSearchForm = (state = INITIAL_MANIFEST_SEARCH_FORM, action) => {
    switch (action.type) {
        case STORE_SEARCH_MANIFESTS:
            return Object.assign({}, state, action.search);
        default:
            return state;
    }
};

const errorMessage = (state = null, action) => {
    switch (action.type) {
        case LOAD_MANIFEST_REJECTED:
        case LOAD_MANIFESTS_REJECTED:
        case LOAD_STOP_MANIFESTS_REJECTED:
            return action.message;
        case CLEAR_ERROR_MESSAGE:
            return null;
        default:
            return state;
    }
};

export default combineReducers({
    byRun,
    entities,
    error,
    errorMessage,
    ids,
    isFetching,
    manifestsSearchForm,
});

// Selectors
export const getManifestById = (state, id) =>
    state.entities.manifests.entities
        ? state.entities.manifests.entities[id]
        : defaultManifestState;
export const getManifests = state => values(state.entities.manifests.entities);
export const getManifestsByStop = state =>
    state.entities.manifests.ids
        ? state.entities.manifests.ids.map(id => state.entities.manifests.entities[id])
        : null;
export const getManifestsByRun = state => state.entities.manifests.byRun;
export const getIds = state => state.entities.manifests.ids; // OR state.entities.manifests.ids
export const getIsFetching = state => state.entities.manifests.isFetching;
export const getError = state => state.entities.manifests.error;
export const getErrorMessage = state => state.entities.manifests.errorMessage;
export const getManifestsSearchForm = state => state.entities.manifests.manifestsSearchForm;

// Action Creators/Thunks
export function loadManifest(id) {
    return function(dispatch) {
        dispatch({ type: LOAD_MANIFEST_PENDING });

        return get(`${API_BASE}${id}`).then(
            response => dispatch({ type: LOAD_MANIFEST_FULFILLED, manifest: response }),
            error =>
                handleApiError(error, dispatch, {
                    type: LOAD_MANIFEST_REJECTED,
                    message: error.message,
                })
        );
    };
}

export function loadManifests(params = null) {
    return function(dispatch) {
        let url = `${API_BASE}`;

        if (params) {
            let queryParams = Object.assign({}, params);

            // We need to convert the runDate that is provided as a query param to
            // the manifest search, when coming from the run details screen ('View manifests')
            if (queryParams.runDate) {
                let momentDate = moment(queryParams.runDate);
                queryParams.runDate = momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : '';
            }
            const stringifiedParams = queryString.stringify(queryParams);
            url += `?${stringifiedParams}&sortParam=manifestNumber&sortDirection=asc`;
        }

        dispatch({ type: LOAD_MANIFESTS_PENDING });

        return get(url).then(
            response =>
                dispatch({
                    type: LOAD_MANIFESTS_FULFILLED,
                    manifests: response.manifestResources,
                }),
            error =>
                handleApiError(error, dispatch, {
                    type: LOAD_MANIFESTS_REJECTED,
                    message: error.message,
                })
        );
    };
}

export function loadManifestsByStop(stopId) {
    return function(dispatch) {
        dispatch({ type: LOAD_STOP_MANIFESTS_PENDING });
        return get(
            `/api/abnormality/stops/${stopId}/manifests?includeGhosts=true&sortParam=manifestNumber&sortDirection=asc`
        ).then(
            response =>
                dispatch({
                    type: LOAD_STOP_MANIFESTS_FULFILLED,
                    manifests: response.manifestResources,
                }),
            error =>
                handleApiError(error, dispatch, {
                    type: LOAD_STOP_MANIFESTS_REJECTED,
                    message: error.message,
                })
        );
    };
}

export function loadManifestsByRun(runId) {
    return function(dispatch) {
        dispatch({ type: LOAD_ABN_MANIFESTS_PENDING });
        return get(`/api/abnormalities/run/${runId}/manifests`).then(
            response =>
                dispatch({
                    type: LOAD_ABN_MANIFESTS_FULFILLED,
                    manifests: response.manifests,
                }),
            error =>
                handleApiError(error, dispatch, {
                    type: LOAD_ABN_MANIFESTS_REJECTED,
                    message: error.message,
                })
        );
    };
}

export function clearManifestsByStop() {
    return function(dispatch) {
        dispatch({ type: CLEAR_STOP_MANIFESTS });
    };
}

export function clearManifestsByRun() {
    return function(dispatch) {
        dispatch({ type: CLEAR_ABN_MANIFESTS });
    };
}

export function clearManifests() {
    return function(dispatch) {
        dispatch({ type: CLEAR_MANIFESTS });
    };
}

export function clearErrorMessage() {
    return function(dispatch) {
        dispatch({ type: CLEAR_ERROR_MESSAGE });
        toastr.removeByType('error');
    };
}

export function storeSearch(search) {
    return function(dispatch) {
        dispatch({ type: STORE_SEARCH_MANIFESTS, search });
    };
}
